import "./App.css";
// import Libraries
import { lazy, Suspense, useEffect } from "react";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { selectVerifiedStatus } from "./redux/user/user.selector";
import { selectSideNavToggle } from "./redux/side-menu/side-menu.selector";
import { createStructuredSelector } from "reselect";
import { InfoCircle } from "react-bootstrap-icons";

// import Components
import Header from "./components/header/header.component";
import Footer from "./components/footer/footer.component";
import SideDrawer from "./components/side-drawer/sidedrawer.component";
import Spinner from "react-bootstrap/Spinner";
import ToastContainerComponent from "./components/toast/toast.component";
import { resetRedux } from "./redux/user/user.actions";

import { toastVersionUpdateMessagePopUp } from "./utils/toastMessagePopup";
import packageInfo from "./../package.json";

// import Pages
const HomePage = lazy(() => import("./pages/home-page/homepage.component"));
const LoginAuditPage = lazy(() => import("./pages/login-audit/loginauditpage.component"));
const LoginPage = lazy(() => import("./pages/login-page/loginpage.component"));
const ResourcesPage = lazy(() => import("./pages/resources-page/resources.component"));
const MivbSmdrEnabler = lazy(() => import("./pages/tools-page/mivb.component"));
const JsonTool = lazy(() => import("./pages/tools-page/jsonConfiguration.component"));
const ViewUsersPage = lazy(() => import("./pages/users-page/viewUsers.component"));
const AlarmsPage = lazy(() => import("./pages/alarms-page/alarms.component"));
const AddCustomerPage = lazy(() => import("./pages/add-customer-page/addCustomer.component"));
const MfaLoginPage = lazy(() => import("./pages/mfa-login-page/mfaLoginPage.component"));
const ViewAnnouncementsPage = lazy(() =>
  import("./pages/announcements-page/viewAnnouncements.component")
);
const FireWall = lazy(() => import("./pages/firewall-page/firewall.component"));

const ImportData = lazy(() => import("./pages/import-data/importdata.component"));

function App({ isUserVerified, sideNavToggle }) {
  const version = packageInfo.version;

  let location = useLocation();

  useEffect(() => {
    initiateUpdateVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const initiateUpdateVersion = async () => {
    if (localStorage.getItem("callation-version")) {
      if (localStorage.getItem("callation-version") !== version) {
        toastVersionUpdateMessagePopUp(
          <div className="new-version-toast-layout">
            <div className="new-version-toast-title">
              <InfoCircle /> New version is available.
            </div>
            <div>
              <button onClick={(e) => updateVersion(e)} className="new-version-toast-button">
                Refresh
              </button>
            </div>
          </div>
        );
      }
    } else localStorage.setItem("callation-version", version);
  };

  const updateVersion = async () => {
    sessionStorage.clear();
    localStorage.setItem("callation-version", version);
    resetRedux();
    window.location.reload();
  };

  return (
    <div className="App">
      <ToastContainerComponent />
      <SideDrawer />
      <div className={"content " + (sideNavToggle ? "active-content" : "")}>
        <Header />
        <Suspense
          fallback={
            <div className="app-spinner-layout">
              <Spinner className="app-spinner-animation" animation="border" />
            </div>
          }
        >
          <Switch>
            <Route
              exact
              path={"/customers"}
              render={() => (!isUserVerified ? <Redirect to="/login" /> : <HomePage />)}
            />
            <Route
              exact
              path={"/login-audit"}
              render={() => (!isUserVerified ? <Redirect to="/login" /> : <LoginAuditPage />)}
            />
            <Route
              exact
              path={"/setup/importdata"}
              render={() => (!isUserVerified ? <Redirect to="/login" /> : <ImportData />)}
            />
            <Route
              exact
              path="/customers/firewall"
              render={() => (!isUserVerified ? <Redirect to="/login" /> : <FireWall />)}
            />

            <Route
              exact
              path={"/login"}
              render={() => (isUserVerified ? <Redirect to="/" /> : <LoginPage />)}
            />
            <Route exact path="/verify" component={MfaLoginPage} />
            <Route
              exact
              path="/resources"
              render={() => (!isUserVerified ? <Redirect to="/login" /> : <ResourcesPage />)}
            />
            <Route
              exact
              path="/resellers/mivb-smdr-enabler"
              render={() => (!isUserVerified ? <Redirect to="/login" /> : <MivbSmdrEnabler />)}
            />
            <Route
              exact
              path="/resellers/json-configuration"
              render={() => (!isUserVerified ? <Redirect to="/login" /> : <JsonTool />)}
            />
            <Route
              exact
              path="/resellers/users"
              render={() => (!isUserVerified ? <Redirect to="/login" /> : <ViewUsersPage />)}
            />
            <Route
              exact
              path="/alarms"
              render={() => (!isUserVerified ? <Redirect to="/login" /> : <AlarmsPage />)}
            />
            <Route
              exact
              path="/add-customer"
              render={() => (!isUserVerified ? <Redirect to="/login" /> : <AddCustomerPage />)}
            />
            <Route
              exact
              path="/announcements"
              render={() =>
                !isUserVerified ? <Redirect to="/login" /> : <ViewAnnouncementsPage />
              }
            />

            <Redirect path="/" to="/customers" />
          </Switch>
        </Suspense>
        <Footer />
      </div>
    </div>
  );
}

const mapStateToProps = createStructuredSelector({
  sideNavToggle: selectSideNavToggle,
  isUserVerified: selectVerifiedStatus,
});

const mapDispatchToProps = (dispatch) => ({
  resetRedux: () => dispatch(resetRedux()),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
